import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'hr' : 'en';
    i18n.changeLanguage(newLang);
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-lg' : 'bg-white/90 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://static.readdy.ai/image/0d2a8bbfaad65247a8e821bed673477a/f50be88335f0ba02d1fbae84a31afe22.jpeg" 
              alt="Sinjavina Skyrace Logo" 
              className="h-12 w-12 object-contain"
            />
            <span className="font-futura font-black text-xl text-primary hidden sm:block">
              SINJAVINA SKYRACE
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`font-futura font-bold text-sm tracking-wide transition-colors whitespace-nowrap cursor-pointer ${
                isActive('/') ? 'text-primary' : 'text-gray-700 hover:text-primary'
              }`}
            >
              {t('common:nav.home')}
            </Link>
            <Link
              to="/registration"
              className={`font-futura font-bold text-sm tracking-wide transition-colors whitespace-nowrap cursor-pointer ${
                isActive('/registration') ? 'text-primary' : 'text-gray-700 hover:text-primary'
              }`}
            >
              {t('common:nav.registration')}
            </Link>
            <Link
              to="/regulations"
              className={`font-futura font-bold text-sm tracking-wide transition-colors whitespace-nowrap cursor-pointer ${
                isActive('/regulations') ? 'text-primary' : 'text-gray-700 hover:text-primary'
              }`}
            >
              {t('common:nav.regulations')}
            </Link>

            {/* Language Selector */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 hover:border-primary transition-colors whitespace-nowrap cursor-pointer"
              aria-label="Toggle language"
            >
              <i className="ri-global-line text-lg"></i>
              <span className="font-futura font-bold text-sm">
                {i18n.language === 'en' ? 'EN' : 'HR'}
              </span>
            </button>

            {/* CTA Button */}
            <a
              href="https://www.avaibooksports.com/inscripcion/sinjavina-skyrace/inscripcion_datos/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary hover:bg-primary-dark text-white font-futura font-bold px-6 py-3 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-lg text-sm whitespace-nowrap cursor-pointer"
            >
              {t('common:nav.registerNow')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-gray-700 hover:text-primary transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden pb-4 border-t border-gray-200 mt-2"
          >
            <div className="flex flex-col gap-4 pt-4">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`font-futura font-bold text-sm tracking-wide transition-colors cursor-pointer ${
                  isActive('/') ? 'text-primary' : 'text-gray-700'
                }`}
              >
                {t('common:nav.home')}
              </Link>
              <Link
                to="/registration"
                onClick={() => setMobileMenuOpen(false)}
                className={`font-futura font-bold text-sm tracking-wide transition-colors cursor-pointer ${
                  isActive('/registration') ? 'text-primary' : 'text-gray-700'
                }`}
              >
                {t('common:nav.registration')}
              </Link>
              <Link
                to="/regulations"
                onClick={() => setMobileMenuOpen(false)}
                className={`font-futura font-bold text-sm tracking-wide transition-colors cursor-pointer ${
                  isActive('/regulations') ? 'text-primary' : 'text-gray-700'
                }`}
              >
                {t('common:nav.regulations')}
              </Link>
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 hover:border-primary transition-colors w-fit cursor-pointer"
              >
                <i className="ri-global-line text-lg"></i>
                <span className="font-futura font-bold text-sm">
                  {i18n.language === 'en' ? 'EN' : 'HR'}
                </span>
              </button>
              <a
                href="https://www.avaibooksports.com/inscripcion/sinjavina-skyrace/inscripcion_datos/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-primary hover:bg-primary-dark text-white font-futura font-bold px-6 py-3 rounded-full transition-all duration-300 text-sm text-center whitespace-nowrap cursor-pointer"
              >
                {t('common:nav.registerNow')}
              </a>
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
}